import type { FormEvent } from "react";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormField } from "@/components/ui/form-field";
import PasswordField from "./PasswordField";

type SignupFormProps = {
  email: string;
  password: string;
  confirmPassword: string;
  loading: boolean;
  success: boolean;
  error?: string;
  onChangeEmail: (value: string) => void;
  onChangePassword: (value: string) => void;
  onChangeConfirmPassword: (value: string) => void;
  onSubmit: (e: FormEvent) => void;
};

export default function SignupForm({
  email,
  password,
  confirmPassword,
  loading,
  success,
  error,
  onChangeEmail,
  onChangePassword,
  onChangeConfirmPassword,
  onSubmit,
}: SignupFormProps) {
  const { t } = useTranslation();
  const passwordMismatch =
    confirmPassword.length > 0 && password !== confirmPassword;

  return (
    <form onSubmit={onSubmit} className="space-y-5" noValidate>
      {error ? <Alert variant="destructive">{error}</Alert> : null}
      {success ? (
        <Alert variant="success">{t("auth.signup_success")}</Alert>
      ) : null}

      <FormField id="signup-email" label={t("auth.email_label")} required>
        <Input
          id="signup-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => onChangeEmail(e.target.value)}
          required
          placeholder={t("auth.email_placeholder")}
        />
      </FormField>

      <FormField id="signup-password" label={t("auth.password_label")} required>
        <PasswordField
          id="signup-password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => onChangePassword(e.target.value)}
          required
          placeholder={t("auth.password_placeholder")}
        />
      </FormField>

      <FormField
        id="signup-confirm-password"
        label={t("auth.confirm_password_label")}
        error={passwordMismatch ? t("auth.password_mismatch") : undefined}
        required
      >
        <PasswordField
          id="signup-confirm-password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => onChangeConfirmPassword(e.target.value)}
          required
          invalid={passwordMismatch}
          placeholder={t("auth.confirm_password_placeholder")}
        />
      </FormField>

      <Button
        type="submit"
        variant="gradient"
        size="lg"
        className="w-full"
        loading={loading}
        disabled={success || passwordMismatch}
      >
        {loading ? t("auth.signing_up") : t("auth.signup_button")}
      </Button>
    </form>
  );
}
